import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import i18n from "@/i18n";

const LangContext = createContext({ lang: "nl", prefix: "", switchLang: () => {} });

const STORAGE_KEY = "ll_lang";
const SUPPORTED = ["nl", "de", "en"];

export const LangProvider = ({ lang, children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
  }, [lang]);

  const value = useMemo(() => {
    const prefix = lang === "nl" ? "" : `/${lang}`;
    const switchLang = (target) => {
      if (!SUPPORTED.includes(target) || target === lang) return;
      // Compute current path without existing lang prefix
      const path = location.pathname.replace(/^\/(de|en)(?=\/|$)/, "") || "/";
      const search = location.search || "";
      const newPrefix = target === "nl" ? "" : `/${target}`;
      const newPath = path === "/" ? newPrefix || "/" : `${newPrefix}${path}`;
      navigate(newPath + search, { replace: false });
    };
    return { lang, prefix, switchLang };
  }, [lang, location.pathname, location.search, navigate]);

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);

export const getInitialLang = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && SUPPORTED.includes(saved)) return saved;
  } catch {
    /* ignore */
  }
  return "nl";
};
